import type { ApolloCache, Reference, StoreObject } from '@apollo/client'
import type { ModifierDetails } from '@apollo/client/cache'

import { UNTAGGED_VALUE } from '~publish/components/FilterByTag'
import type { ResultOf } from '~publish/graphql'
import type { GetPostList } from '~publish/pages/AllChannels/PostList/usePaginatedPostList'

import { postSatisfiesTagFilter } from './isPostSatisfyingFilters'

type PostsEdge = NonNullable<
  NonNullable<ResultOf<typeof GetPostList>['posts']>['edges']
>[number]

/**
 * Reconstructs a cached `posts(...)` connection's tag-filter selection from its
 * Apollo `storeFieldName`, in the UI tag-id form that `postSatisfiesTagFilter`
 * expects (the "untagged" sentinel plus any selected tag ids).
 *
 * Returns `null` when the connection has no tag filter — every post belongs
 * regardless of its tags — so callers can leave those lists untouched.
 *
 * The `posts` field is keyed by its `input` arg (see the Apollo type policy),
 * so the args serialise into the store field name as
 * `posts:{"input":{"filter":{"tags":{...}}}}`.
 */
export function parsePostsConnectionTagFilter(
  storeFieldName: string,
): string[] | null {
  if (!storeFieldName.startsWith('posts:')) {
    return null
  }

  let parsed: unknown
  try {
    parsed = JSON.parse(storeFieldName.slice('posts:'.length))
  } catch {
    return null
  }

  const tags = readTagComparator(parsed)
  if (!tags) {
    return null
  }

  const tagFilterIds = [...(tags.isEmpty ? [UNTAGGED_VALUE] : []), ...tags.in]
  return tagFilterIds.length > 0 ? tagFilterIds : null
}

function readTagComparator(
  value: unknown,
): { isEmpty: boolean; in: string[] } | null {
  if (!isRecord(value)) return null
  const input = value.input
  if (!isRecord(input)) return null
  const filter = input.filter
  if (!isRecord(filter)) return null
  const tags = filter.tags
  if (!isRecord(tags)) return null

  const tagIds = Array.isArray(tags.in)
    ? tags.in.filter((id): id is string => typeof id === 'string')
    : []
  return { isEmpty: tags.isEmpty === true, in: tagIds }
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null
}

/**
 * After a post's tags change, removes it from the cached tag-filtered lists it
 * no longer belongs to. Runs when the tag menu closes (see `PostCardTags`);
 * until then both removal paths hold the post visible so several tags can be
 * added in a row.
 *
 * Scoped per cached connection rather than clearing the post from every
 * `posts(...)` result: a list with no tag filter (e.g. the "all posts" view) or
 * one whose tag filter the post still satisfies keeps the post — only lists it
 * has aged out of are pruned. The post's current tags are read from the cache,
 * so this must run once the tag mutation has settled.
 */
export function removePostFromMismatchedTagLists(
  cache: ApolloCache,
  postId: string,
): void {
  cache.modify({
    fields: {
      posts(
        existingConnection: Reference | StoreObject,
        { readField, toReference, storeFieldName }: ModifierDetails,
      ) {
        if (existingConnection && '__ref' in existingConnection) {
          return existingConnection
        }

        const tagFilterIds = parsePostsConnectionTagFilter(storeFieldName)
        if (!tagFilterIds) {
          return existingConnection
        }

        const postRef = toReference({ __typename: 'Post', id: postId })
        if (!postRef) {
          return existingConnection
        }

        const postTagIds = (
          readField<ReadonlyArray<Reference | StoreObject>>('tags', postRef) ??
          []
        )
          .map((tag) => readField<string>('id', tag))
          .filter((id): id is string => typeof id === 'string')

        if (postSatisfiesTagFilter(postTagIds, tagFilterIds)) {
          return existingConnection
        }

        const edges = readField<PostsEdge[]>('edges', existingConnection) ?? []
        return {
          ...existingConnection,
          edges: edges.filter(
            (edge: PostsEdge) => readField('id', edge.node) !== postId,
          ),
        }
      },
    },
  })
}
